import { useNavigate } from 'react-router-dom';
import {
  Button,
  DevToolsPortal,
  LazyLoadedDevTools,
  Paragraph,
} from '@quickmd-llc/design-system';

import { useApplicationStore } from '@data';
import { DEV_TOOLS_SANDBOX_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import { PatientWebUrlSection } from './components/PatientWebUrlSection';
import { isDevToolsEnabled } from './isDevToolsEnabled';
import { FlagsSection } from './components/FlagsSection';
import { IdVerificationSection } from './components/IdVerificationSection';
import { IntakeQuizSection } from './components/IntakeQuizSection';
import { RouteSelect } from './components/RouteSelect';
import { SelectVisitSection } from './components/SelectedVisitSection';

// Static environment data — no provider dependencies, safe for outermost rendering
const staticEnvironmentData = {
  'App': 'onboarding',
  'Environment': import.meta.env.MODE,
  'API URL': import.meta.env.VITE_BASE_API_URL,
  'Commit Hash': import.meta.env.VITE_GIT_COMMIT_HASH,
  'Branch': import.meta.env.VITE_GIT_BRANCH_NAME,
};

/**
 * DevTools shell — renders outside error boundaries for crash resilience.
 * Console, network, storage, and environment panels survive app crashes.
 * Pass this to WebAppErrorShell's `devTools` prop.
 */
export function DevToolsShell() {
  return (
    <LazyLoadedDevTools
      isDevToolsEnabled={isDevToolsEnabled()}
      environmentData={staticEnvironmentData}
    />
  );
}

/**
 * Provider-dependent DevTools content — portals into the DevTools App tab.
 * Rendered inside the app tree (with Router, stores, etc.) so it has access to all providers.
 * If the app crashes, this content disappears but the core DevTools panels survive.
 */
export function DevToolsAppContent() {
  const navigate = useNavigate();
  const seedStore = useApplicationStore((state) => state.seedStore);
  const patient = useApplicationStore((s) => s.patient);
  const resetDevTools = useApplicationStore((s) => s.devTools.resetDevTools);

  if (!isDevToolsEnabled()) return null;

  function handleClearLocalStorage() {
    localStorage.clear();
  }

  return (
    <DevToolsPortal>
      <div>
        <Paragraph size="xs" className="mb-1 font-semibold">
          State Management
        </Paragraph>

        <div className="flex gap-2">
          <Button priority="secondary" size="s" onClick={seedStore}>
            Seed Store
          </Button>

          <Button
            priority="secondary"
            size="s"
            onClick={handleClearLocalStorage}
          >
            Clear Local Storage
          </Button>
          <Button priority="secondary" size="s" onClick={resetDevTools}>
            Reset Dev Tools
          </Button>
        </div>
      </div>

      <div>
        <Paragraph size="xs" className="mb-1 font-semibold">
          Auth
        </Paragraph>

        <div className="text-sm">
          {patient?.patientProfile?.patientId ? (
            <>
              <div>
                <span>Patient ID:</span> {patient.patientProfile.patientId}
              </div>
              <div>
                <span>Patient Email:</span> {patient.patientProfile.email}
              </div>
            </>
          ) : (
            <div>Unauthenticated</div>
          )}
        </div>
      </div>

      <SelectVisitSection />

      <RouteSelect />

      <IdVerificationSection />

      <PatientWebUrlSection />

      <FlagsSection />

      <IntakeQuizSection />

      <Button
        priority="secondary"
        size="s"
        onClick={() => navigate(DEV_TOOLS_SANDBOX_ROUTE_PATHNAME)}
      >
        Sandbox
      </Button>

      <div>
        <Paragraph size="xs" className="mb-1 font-semibold">
          Env
        </Paragraph>

        <div className="text-xs">
          <div>
            ENV/MODE: <code>{import.meta.env.MODE}</code>
          </div>
          <div>
            BASE_API_URL: <code>{import.meta.env.VITE_BASE_API_URL}</code>
          </div>
          <div>
            COMMIT DATE: <code>{import.meta.env.VITE_GIT_COMMIT_DATE}</code>
          </div>
          <div>
            COMMIT HASH: <code>{import.meta.env.VITE_GIT_COMMIT_HASH}</code>
          </div>
          <div>
            BRANCH NAME: <code>{import.meta.env.VITE_GIT_BRANCH_NAME}</code>
          </div>
        </div>
      </div>
    </DevToolsPortal>
  );
}
